// frontend/src/components/HotelInfo/SpaSetting.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Stack, Paper, Typography, TextField, Switch, FormControlLabel,
  Button, Divider, MenuItem, Container, FormControl, Select, InputLabel,
  Autocomplete, IconButton, Chip, Snackbar, Alert, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
const TIMEZONES = [
  'Asia/Phnom_Penh', 'Asia/Bangkok', 'Asia/Singapore', 'Asia/Kuala_Lumpur',
  'Asia/Jakarta', 'Asia/Ho_Chi_Minh', 'Asia/Manila', 'Asia/Hong_Kong',
  'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Kolkata', 'Asia/Dubai',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Amsterdam', 'Europe/Zurich',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'America/Vancouver',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
  'Africa/Johannesburg', 'UTC'
];
import { getSpaSettings, updateSpaSettings } from '../../api/spaApi';

const defaultState = {
  taxRate: 0,
  serviceChargeRate: 0,
  paymentTypes: [],
  maxGuestsPerSlot: 3, // limit simultaneous bookings per time slot
  bookingPolicy: {
    allowSameDay: true,
    minAdvanceHours: 1,       // bookings must be made at least this many units before preferred time
    minAdvanceUnit: 'hours',  // 'hours' | 'minutes'
    allowWalkIn: true,
    notes: '',
  },
  cancellationPolicy: {
    allowed: false,
    windowHours: 24,
    feeType: 'none', // 'none' | 'percentage' | 'fixed'
    feeValue: 0,
    notes: '',
  },
  autoConfirm: false,
  bufferMinutesBetweenTreatments: 0,
  termsAndConditions: '',
  timezone: 'Asia/Phnom_Penh',
};

export default function SpaSetting({ propertyId }) {
  const [form, setForm] = useState(defaultState);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialForm, setInitialForm] = useState(defaultState);
  const [newPaymentType, setNewPaymentType] = useState('');
  const [savedToast, setSavedToast] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const data = await getSpaSettings({ propertyId });
      const merged = (() => {
        const d = data || {};
        const legacyPolicy = {
          allowed: Boolean(d.cancellationPolicy && d.cancellationPolicy !== 'none'),
          windowHours: Number(d.cancellationCutoffHours ?? 24),
          feeType: typeof d.cancellationFeePercent === 'number' && d.cancellationFeePercent > 0 ? 'percentage' : 'none',
          feeValue: Number(d.cancellationFeePercent ?? 0),
          notes: d.cancellationNotes || '',
        };
        return {
          ...defaultState,
          ...d,
          paymentTypes: Array.isArray(d.paymentTypes) ? d.paymentTypes : [],
          maxGuestsPerSlot: d.maxGuestsPerSlot ?? defaultState.maxGuestsPerSlot,
          bookingPolicy: {
            ...defaultState.bookingPolicy,
            ...(d.bookingPolicy || {}),
          },
          cancellationPolicy: {
            ...defaultState.cancellationPolicy,
            ...(d.cancellationPolicy && typeof d.cancellationPolicy === 'object' ? d.cancellationPolicy : legacyPolicy),
          },
        };
      })();
      setForm(merged);
      setInitialForm(merged);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const onChange = (k) => (e) => {
    const v = e?.target?.type === 'number'
      ? Number(e.target.value || 0)
      : e?.target?.type === 'checkbox'
      ? !!e.target.checked
      : e?.target?.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const handleCancellationToggle = (event) => {
    const allowed = event.target.checked;
    setForm((s) => ({
      ...s,
      cancellationPolicy: { ...s.cancellationPolicy, allowed },
    }));
  };

  const handleCancellationInput = (field) => (event) => {
    let value = event.target.value;
    if (field === 'windowHours' || field === 'feeValue') {
      const n = Number(value);
      value = Number.isNaN(n) ? 0 : Math.max(0, n);
    }
    setForm((s) => ({
      ...s,
      cancellationPolicy: { ...s.cancellationPolicy, [field]: value },
    }));
  };

  const handleBookingToggle = (field) => (event) => {
    const checked = !!event.target.checked;
    setForm((s) => ({
      ...s,
      bookingPolicy: { ...s.bookingPolicy, [field]: checked },
    }));
  };

  const handleBookingInput = (field) => (event) => {
    let value = event.target.value;
    if (field === 'minAdvanceHours') {
      const n = Number(value);
      value = Number.isNaN(n) ? 0 : Math.max(0, n);
    }
    setForm((s) => ({
      ...s,
      bookingPolicy: { ...s.bookingPolicy, [field]: value },
    }));
  };

  const handleAddPaymentType = () => {
    const t = (newPaymentType || '').trim();
    if (!t) return;
    setForm((s) => (
      (s.paymentTypes || []).includes(t)
        ? s
        : { ...s, paymentTypes: [ ...(s.paymentTypes || []), t ] }
    ));
    setNewPaymentType('');
  };

  const handleDeletePaymentType = (t) => {
    setForm((s) => ({
      ...s,
      paymentTypes: (s.paymentTypes || []).filter((x) => x !== t),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateSpaSettings({ propertyId, payload: form });
      const next = updated || form;
      setForm(next);
      setInitialForm(next);
      setSavedToast(true);
    } catch (e) {
      setErrorMsg('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>Spa Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure spa tax, policy & operational preferences
          </Typography>
        </Stack>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Tax Settings</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Tax Rate (%)"
            type="number"
            value={form.taxRate}
            onChange={onChange('taxRate')}
            fullWidth
          />
          <TextField
            label="Service Charge (%)"
            type="number"
            value={form.serviceChargeRate}
            onChange={onChange('serviceChargeRate')}
            fullWidth
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Payment Options</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            placeholder="Add new payment method..."
            value={newPaymentType}
            onChange={(e) => setNewPaymentType(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPaymentType()}
            fullWidth
          />
          <IconButton aria-label="Add payment type" onClick={handleAddPaymentType} color="primary" sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {(form.paymentTypes || []).map((t) => (
            <Chip key={t} label={t} onDelete={() => handleDeletePaymentType(t)} sx={{ bgcolor: 'grey.100', '& .MuiChip-deleteIcon': { color: 'text.secondary' }, '&:hover': { bgcolor: 'grey.200' } }} />
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Booking Policy</Typography>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={!!form.bookingPolicy?.allowSameDay} onChange={handleBookingToggle('allowSameDay')} />}
            label="Allow same‑day bookings"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
            <TextField
              label="Minimum advance"
              type="number"
              value={form.bookingPolicy?.minAdvanceHours ?? 0}
              onChange={handleBookingInput('minAdvanceHours')}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                label="Unit"
                value={form.bookingPolicy?.minAdvanceUnit || 'hours'}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    bookingPolicy: { ...s.bookingPolicy, minAdvanceUnit: e.target.value },
                  }))
                }
              >
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="minutes">Minutes</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <FormControlLabel
            control={<Switch checked={!!form.bookingPolicy?.allowWalkIn} onChange={handleBookingToggle('allowWalkIn')} />}
            label="Allow walk‑in (no pre‑booking)"
          />
          <TextField
            label="Notes (internal)"
            value={form.bookingPolicy?.notes || ''}
            onChange={handleBookingInput('notes')}
            multiline
            minRows={2}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Cancellation Policy</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={form.cancellationPolicy?.allowed || false}
              onChange={handleCancellationToggle}
            />
          }
          label="Allow guests to cancel their bookings"
          sx={{ mb: 2 }}
        />

        {form.cancellationPolicy?.allowed && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Cancellation window (hours before)"
              type="number"
              value={form.cancellationPolicy?.windowHours ?? 24}
              onChange={handleCancellationInput('windowHours')}
              inputProps={{ min: 0 }}
            />
            <FormControl fullWidth>
              <InputLabel>Cancellation fee type</InputLabel>
              <Select
                label="Cancellation fee type"
                value={form.cancellationPolicy?.feeType || 'none'}
                onChange={handleCancellationInput('feeType')}
              >
                <MenuItem value="none">No fee</MenuItem>
                <MenuItem value="percentage">Percentage of order total</MenuItem>
                <MenuItem value="fixed">Fixed amount</MenuItem>
              </Select>
            </FormControl>
            {form.cancellationPolicy?.feeType !== 'none' && (
              <TextField
                label={form.cancellationPolicy?.feeType === 'percentage' ? 'Fee (%)' : 'Fee amount'}
                type="number"
                value={form.cancellationPolicy?.feeValue ?? 0}
                onChange={handleCancellationInput('feeValue')}
                inputProps={{ min: 0 }}
              />
            )}
            <TextField
              label="Additional notes"
              value={form.cancellationPolicy?.notes || ''}
              onChange={handleCancellationInput('notes')}
              multiline
              minRows={3}
            />
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Operational Preferences</Typography>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <FormControlLabel
            control={<Switch checked={form.autoConfirm} onChange={onChange('autoConfirm')} />}
            label="Auto-confirm spa bookings"
          />
          <TextField
            label="Max guests per time slot"
            type="number"
            value={form.maxGuestsPerSlot}
            onChange={onChange('maxGuestsPerSlot')}
            fullWidth
          />
          <TextField
            label="Buffer between treatments (mins)"
            type="number"
            value={form.bufferMinutesBetweenTreatments}
            onChange={onChange('bufferMinutesBetweenTreatments')}
            fullWidth
          />
          <Autocomplete
            options={TIMEZONES}
            value={form.timezone || ''}
            onChange={(e, v) => setForm((s) => ({ ...s, timezone: v || '' }))}
            renderInput={(params) => (
              <TextField {...params} label="Timezone" placeholder="Select timezone" fullWidth />
            )}
          />
          <TextField
            multiline
            minRows={3}
            label="Terms & Conditions (shown to guests)"
            value={form.termsAndConditions}
            onChange={onChange('termsAndConditions')}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={load} disabled={loading || saving}>Reset</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!dirty || saving}
            sx={{
              px: 3,
              textTransform: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              bgcolor: !dirty ? 'grey.300' : 'primary.main',
              color: !dirty ? 'text.disabled' : 'white',
              '&:hover': {
                bgcolor: !dirty ? 'grey.400' : 'primary.dark',
              },
            }}
          >
            {saving ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} sx={{ color: 'inherit' }} />
                <span>Saving…</span>
              </Stack>
            ) : 'Save Changes'}
          </Button>
        </Stack>
      </Paper>
      <Snackbar
        open={savedToast}
        autoHideDuration={2000}
        onClose={() => setSavedToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSavedToast(false)}
          severity="success"
          sx={{
            width: '100%',
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            color: '#2E7D32',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            fontWeight: 500,
          }}
        >
          Settings saved
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={2500}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setErrorMsg('')}
          severity="error"
          sx={{
            width: '100%',
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            color: '#D32F2F',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            fontWeight: 500,
          }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}